import React from 'react';
import { Info } from 'lucide-react';
import { Tooltip } from 'common/components';
import { formatCount, STATUS_ICON_THEME, type CallStatusSummaryCard } from '../../callStatusSummary.utils';

import styles from './CallStatusCard.module.scss';

export type CallStatusCardProps = {
    card: CallStatusSummaryCard;
    isSelected?: boolean;
    onClick?: (cardId: string) => void;
    /** When set, replaces the count / percent row (e.g. avg duration). */
    primaryValue?: string;
    showPercent?: boolean;
};

export const CallStatusCard: React.FC<CallStatusCardProps> = ({
    card,
    isSelected = false,
    onClick,
    primaryValue,
    showPercent = true,
}) => {
    const theme = STATUS_ICON_THEME[card.variant];
    const Icon = theme.icon;
    const interactive = typeof onClick === 'function';

    const className = [styles.card, interactive ? '' : styles.cardStatic, isSelected ? styles.cardSelected : '']
        .filter(Boolean)
        .join(' ');

    const cssVars = {
        '--status-color': theme.color,
        '--status-soft-bg': theme.softBackground,
    } as React.CSSProperties;

    const header = (
        <div className={styles.cardHeader}>
            <span className={styles.label}>
                <span className={styles.statusIconChip}>
                    <Icon size={14} aria-hidden='true' />
                </span>
                {card.label}
            </span>
            <Tooltip content={card.tooltip} maxWidth={350}>
                <Info
                    size={14}
                    className={styles.infoIcon}
                    tabIndex={0}
                    aria-label={`${card.label} info`}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                />
            </Tooltip>
        </div>
    );

    const valueRow =
        primaryValue != null ? (
            <span className={styles.count}>{primaryValue}</span>
        ) : (
            <div className='flex gap-xs items-end mt-auto'>
                <span className={styles.count}>{formatCount(card.count)}</span>
                {showPercent ? (
                    <>
                        /<span className={styles.percent}>{card.percent}%</span>
                    </>
                ) : null}
            </div>
        );

    if (interactive) {
        return (
            <button
                type='button'
                role='tab'
                id={`call-status-tab-${card.id}`}
                aria-selected={isSelected}
                aria-controls={`call-status-panel-${card.id}`}
                className={className}
                style={cssVars}
                onClick={() => onClick(card.id)}
                data-testid={`call-status-card-${card.id}`}
            >
                {header}
                {valueRow}
            </button>
        );
    }

    return (
        <article
            className={className}
            style={cssVars}
            data-testid={`call-status-card-${card.id}`}
            aria-label={card.label}
        >
            {header}
            {valueRow}
        </article>
    );
};
